import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import counties from '../data/ga-counties.json';

// The board's map view — the geography of the day, for a 75" panel read from 10-15ft.
//
// Built as a DIAGRAM, not a map. At that distance road names, town labels and street
// detail are all invisible, so they are only clutter; what reads is big coloured dots
// and how they are arranged. The basemap is pushed right back with a filter so the
// crew colours are the only strong thing on screen.
//
// It answers the one question the crew columns cannot: WHO IS NEAR WHAT. "Who could
// swing by Ringgold this afternoon" is instant here and unanswerable in a column.
//
// Two kinds of dot, and the difference is the honest part:
//   SOLID  — a collector reporting its own position. Where they actually are.
//   RING   — no collector, so the crew is drawn at the job they clocked into. That is
//            where they said they'd be, which is not the same claim.
// Conflating the two would make a guess look like an observation on a wall a whole
// office reads without questioning it.

// Deliberately not interactive. It self-frames on every refresh, and on a touchscreen
// an interactive map is one stray hand away from being left zoomed into the Atlantic
// with nobody noticing for a week.
// County outlines carry the geographic context that road detail cannot at this
// distance: a county is a big shape with a name, and "they're in Gordon today" is a
// sentence the office already thinks in. Drawn behind everything, no fill, so it frames
// the dots instead of competing with them.
//
// Statewide, because the work is: 193 coordinated jobs run from lat 31.3 to 35.1 and
// from the Alabama line nearly to the coast. Most days are NW Georgia, but the board
// must not lie about a job in Baldwin County by having no map under it.
//
// 171 names on screen at statewide zoom is mush, so labels only appear once counties
// are big enough to read. Zoomed out you get the shapes, which is all that resolution
// can honestly carry anyway.
const COUNTY_LABEL_MIN_ZOOM = 9;
const COUNTY_STYLE = { color: '#3b6389', weight: 1.5, opacity: 0.75, fill: false, interactive: false };

const countyLabel = (feature, layer) => {
  layer.bindTooltip(feature.properties.name, {
    permanent: true,
    direction: 'center',
    className: 'cc-county-label',
    // Leaflet otherwise reserves pointer space for tooltips, which would sit over the
    // markers on a display where nothing is clickable anyway.
    interactive: false,
  });
};

const MAP_OPTS = {
  zoomControl: false, dragging: false, scrollWheelZoom: false, doubleClickZoom: false,
  touchZoom: false, boxZoom: false, keyboard: false, attributionControl: false,
};

function dot({ color, label, solid, dim }) {
  // The icon box only positions the anchor; the visible dot is sized in CSS with
  // clamp() so it scales with the panel like every other element on this board. Fixed
  // pixels looked right on a laptop and would have been 0.58in on the 75" wall, where
  // ~1.5in is the threshold for glancing at something from 15ft.
  const BOX = 40;
  const cls = ['cc-map-dot', solid ? 'is-solid' : 'is-ring', dim ? 'is-dim' : ''].join(' ');
  return L.divIcon({
    className: 'cc-map-pin',
    iconSize: [BOX, BOX],
    iconAnchor: [BOX / 2, BOX / 2],
    html:
      `<div class="${cls}" style="--dot:${color}"></div>` +
      `<div class="cc-map-label" style="color:${color}">${label}</div>`,
  });
}

// Same reasoning as the crew dots: sized in CSS so it isn't a speck on the wall.
const jobPin = L.divIcon({
  className: 'cc-map-pin',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  html: '<div class="cc-map-job"></div>',
});

/** Hides county names when they'd be unreadable, via a class on the map container. */
function LabelZoomGate() {
  const map = useMap();
  React.useEffect(() => {
    const apply = () => {
      map.getContainer().classList.toggle(
        'cc-zoomed-out', map.getZoom() < COUNTY_LABEL_MIN_ZOOM
      );
    };
    map.on('zoomend', apply);
    apply();
    return () => map.off('zoomend', apply);
  }, [map]);
  return null;
}

/** Keeps the view framed on today's work, every time the data moves. */
function Fit({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 12, { animate: false });
      return;
    }
    map.fitBounds(L.latLngBounds(points).pad(0.25), { animate: false });
  }, [map, points]);
  return null;
}

export default function CommandCenterMap({ board }) {
  // Escaping matters: crew names come from the database and go into divIcon HTML.
  const esc = (s) => String(s || '').replace(/[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const markers = useMemo(() => {
    const out = [];
    for (const crew of board.crews || []) {
      const d = crew.device;
      if (d && d.lat != null && d.lng != null) {
        // Observed. The server only sends a position it is willing to stand behind;
        // when a collector has gone quiet it flags the position stale rather than
        // withholding it, and the dot dims to say "this is where they were".
        out.push({
          key: `dev-${crew.id}`, pos: [d.lat, d.lng], color: crew.color,
          label: esc(crew.name), solid: true,
          dim: d.signal === 'stale' || d.stale_position === true,
        });
      } else if (crew.current?.latitude != null && crew.current?.longitude != null) {
        // Inferred from the timer: this is the JOB's location, not a reading.
        out.push({
          key: `job-${crew.id}`, pos: [crew.current.latitude, crew.current.longitude],
          color: crew.color, label: esc(crew.name), solid: false,
          dim: crew.status === 'off' || crew.status === 'paused',
        });
      }
    }
    return out;
  }, [board]);

  // Today's other work, so the dots have context to sit in.
  const jobs = useMemo(() => {
    const shown = new Set();
    const out = [];
    const add = (j) => {
      if (!j || j.latitude == null || j.longitude == null || shown.has(j.job_id)) return;
      shown.add(j.job_id);
      out.push({ key: j.job_id, pos: [j.latitude, j.longitude] });
    };
    for (const crew of board.crews || []) {
      (crew.queue || []).forEach(add);
      if (crew.current) add(crew.current);
    }
    (board.unassigned || []).forEach(add);
    return out;
  }, [board]);

  const allPoints = useMemo(
    () => [...markers.map((m) => m.pos), ...jobs.map((j) => j.pos)],
    [markers, jobs]
  );

  if (!allPoints.length) {
    // Nothing has coordinates yet. Saying so beats an empty map of Georgia, which
    // reads as "everyone has vanished".
    return (
      <div className="cc-map-wrap">
        <div className="cc-center">Nothing on the map today — no job coordinates yet.</div>
      </div>
    );
  }

  return (
    <div className="cc-map-wrap">
      <MapContainer
        className="cc-map"
        center={allPoints[0]}
        zoom={11}
        {...MAP_OPTS}
      >
        {/* Greyed and dimmed via CSS so the crew colours carry the whole signal. */}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          className="cc-map-tiles"
        />
        {/* Before the markers so county lines and names sit underneath the dots. */}
        <GeoJSON data={counties} style={COUNTY_STYLE} onEachFeature={countyLabel} />
        <LabelZoomGate />
        <Fit points={allPoints} />
        {jobs.map((j) => (
          <Marker key={`j-${j.key}`} position={j.pos} icon={jobPin} interactive={false} />
        ))}
        {markers.map((m) => (
          <Marker key={m.key} position={m.pos} icon={dot(m)} interactive={false} />
        ))}
      </MapContainer>

      <div className="cc-map-legend">
        <span><i className="cc-key-solid" /> collector reporting</span>
        <span><i className="cc-key-ring" /> clocked in, no collector</span>
        <span><i className="cc-key-job" /> today&rsquo;s jobs</span>
        {/* OSM requires attribution; kept small rather than absent. */}
        <span className="cc-map-attr">© OpenStreetMap</span>
      </div>
    </div>
  );
}
